// R6 — reading view shell. The panes themselves live in `reading-panes.tsx`,
// shared with the `/admin` editor workbench; this file is the READER around
// them. Retelling is the primary column; the original opens beside it (desktop:
// a second independent scroll pane, synced by the anchors) or INSIDE it (narrow:
// one scroll column with the original woven in as collapsible insets — see
// interleaved-column.tsx). Anchors are invisible — they drive the weave and the
// sync silently, no gutter markers.
//
// What this file owns, and `ReadingPanes` deliberately does not: the breakpoint
// (it calls `useMediaQuery` and hands down `interleaved`/`showPaneBars`, so the
// workbench can render two panes at any width), font-size PERSISTENCE (the panes
// report changes up through `onFontChange`; `setPref` is called here), the
// `?original=`/`?sync=` URL round-tripping, bookmarks, `PageTurnNav`, the
// copyright footer and copy handler, and the premium gate.
//
// The two-pane control this file still owns — the hard-align toggle — is
// hidden on narrow screens rather than disabled: there is one column there, so
// it has nothing to align. The size-link toggle used to sit beside it and is
// gone from this file entirely: it now rides on the divide between the two
// text-size controls it links (`reading-panes.tsx`), and is absent on narrow
// screens for the same reason, by the same predicate.
//
// What survives on narrow is a single unlabeled text-size control in its own
// bar, driving that one size (both texts in the interleaved layout); it is the
// reader's only size affordance, so hiding it would leave a low-vision reader
// with no way to read at all. Because it goes through the retelling pane the
// persisted `FontSizePrefs` keeps honouring `linked` exactly as the desktop
// panes do.
//
// Reading interactions emit canonical `reading.*` wide events via the shared
// logger (never the raw SDK). The original pane is rendered selectable for
// non-English sources and wired as a keyboard gloss entry point
// (`DocumentPane`'s `glossEntry` prop); the popover itself is `SelectionLayer`,
// mounted below, gated on the same `glossEligible` condition (never for `en`).

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  Columns2,
  Rows2,
  Trash2,
} from "lucide-react";
import { AppNav } from "@/components/app-nav";
import { Button } from "@bindery/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bindery/ui/components/ui/popover";
import { Separator } from "@bindery/ui/components/ui/separator";
import { Toggle } from "@bindery/ui/components/ui/toggle";
import { copyrightNotice } from "@/lib/copyright";
import { flushPerformanceChunk, getDeviceContext, useLogger } from "@/logging";
import { getPref, setPref } from "@/lib/prefs";
import type { RetellingLocation } from "@bindery/convex/model/location";
import {
  DEFAULT_FONT,
  type FontSizePrefs,
  type PaneId,
  type SyncMode,
} from "@bindery/shared/corpus/reader";
import {
  addBookmark,
  getPosition,
  isBookmarked,
  listBookmarks,
  removeBookmark,
  savePosition,
} from "./bookmarks";
import { MatterLoader } from "./matter-view";
import { UpsellPlate } from "./premium-gate";
import { lockedChapter, useEntitlementGate } from "./locked-unit";
import { useReadingTelemetry } from "./use-reading-telemetry";
import { adjacentUnits, type ReadingUnit } from "./reading-order";
import {
  ReadingFrame,
  ReadingNotFound,
  ReadingSkeleton,
} from "./reading-frame";
import { ReadingPanes } from "./reading-panes";
import { paneColumnKey } from "./reading-panes-context";
import { SelectionLayer } from "./selection-layer";
import { PageTurnNav, UnitNavButtons } from "./unit-nav";
import { VolumeEnd } from "./volume-end";
import {
  useCatalog,
  useChapterData,
  usePremiumChapterData,
  type ChapterData,
} from "./use-corpus";
import { findBook, findChapter } from "@/lib/corpus";
import type {
  CatalogBook,
  CatalogChapter,
} from "@bindery/shared/corpus/catalog";
import { useCopyHandler } from "./use-copy-handler";
import { useMediaQuery } from "./use-media-query";
import { useReadingNav } from "./use-reading-nav";

function BookmarksMenu({
  bookmarks,
  currentBookmarked,
  onToggle,
  onJump,
  onRemove,
}: {
  bookmarks: { location: RetellingLocation; label?: string }[];
  currentBookmarked: boolean;
  onToggle: () => void;
  onJump: (location: RetellingLocation) => void;
  onRemove: (location: RetellingLocation) => void;
}) {
  return (
    // A single grouped "chip": the bookmark toggle and its count read as one
    // control, not two loose buttons touching with no gap between them.
    <div className="bg-muted/50 flex items-center gap-0.5 rounded-md p-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-pressed={currentBookmarked}
        aria-label={
          currentBookmarked ? "Remove bookmark here" : "Bookmark here"
        }
        onClick={onToggle}
      >
        {currentBookmarked ? (
          <BookmarkCheck className="text-primary size-4" />
        ) : (
          <Bookmark className="size-4" />
        )}
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="w-auto min-w-8 px-2 font-serif text-xs tabular-nums"
            aria-label="Show bookmarks"
          >
            {bookmarks.length}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          collisionPadding={16}
          className="w-72 max-w-[calc(100vw-2rem)] p-2"
        >
          <p className="text-muted-foreground px-2 py-1.5 font-serif text-xs tracking-wide uppercase">
            Bookmarks
          </p>
          {bookmarks.length === 0 ? (
            <p className="text-muted-foreground px-2 py-3 font-serif text-sm">
              No bookmarks in this chapter yet.
            </p>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {bookmarks.map((mark) => (
                <li
                  key={`${mark.location.blockId}.${String(mark.location.offset)}`}
                  className="flex items-center gap-1"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onJump(mark.location);
                    }}
                    className="hover:bg-accent flex-1 rounded-md px-2 py-2 text-left font-serif text-sm"
                  >
                    {mark.label ?? `Position ${mark.location.blockId}`}
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove bookmark"
                    onClick={() => {
                      onRemove(mark.location);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ChapterReader({ data }: { data: ChapterData }) {
  const { book, chapter, retellingModel, originalModel, anchors } = data;
  const unit: ReadingUnit = {
    kind: "chapter",
    number: chapter.number,
    title: chapter.title,
    premium: chapter.premium === true,
  };
  const { prev, next } = adjacentUnits(book, unit);
  const log = useLogger();
  // Keep the latest logger in a ref so the interaction handlers and the
  // once-per-open effect don't refire when the logger identity changes
  // (react-useeffect-discipline; ref updated in an effect, never during render).
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  const retellingRef = useRef<HTMLDivElement>(null);
  const originalRef = useRef<HTMLDivElement>(null);

  // `?original=`/`?sync=` round-trip the original-pane toggle and sync mode,
  // the same way useReadingNav round-trips `?loc=`: the URL param wins on
  // load when present, else fall back to the prior default/pref.
  const [searchParams, setSearchParams] = useSearchParams();
  const [showOriginal, setShowOriginal] = useState(
    () => searchParams.get("original") === "1",
  );
  const [syncMode, setSyncMode] = useState<SyncMode>(() => {
    // Hard alignment is the default, so the URL carries `?sync=interpolated`
    // only when the reader turns it off — the param marks the exception, not
    // the rule. `?sync=hard` is still read so links minted before the flip
    // (and any bookmark holding one) resolve to what they meant at the time.
    const fromUrl = searchParams.get("sync");
    if (fromUrl === "interpolated") return "interpolated";
    if (fromUrl === "hard") return "hard";
    return getPref("syncMode", "hard");
  });
  const [fontSize, setFontSize] = useState<FontSizePrefs>(() =>
    getPref("fontSize", DEFAULT_FONT),
  );
  const [bookmarks, setBookmarks] = useState(() =>
    listBookmarks(book.slug, chapter.number),
  );

  const isNarrow = useMediaQuery("(max-width: 767px)");
  const glossEligible = book.sourceLanguage !== "en";
  // One scroll column with the original woven in, instead of two panes.
  const interleaved = showOriginal && isNarrow;
  // WHICH component is rendering the retelling column. `InterleavedColumn` and
  // `DocumentPane` occupy the same position in the tree, so flipping this
  // unmounts one and mounts the other: `retellingRef.current` becomes a new
  // node, and every effect bound to it has to re-bind or it keeps listening to
  // a node that is no longer in the document.
  const columnKey = paneColumnKey(interleaved);

  // Block ids in document order — the index basis for "furthest reached" and
  // the completion denominator. Derived during render (never an effect); the
  // model is stable for the life of the chapter, so this is a `useMemo` for the
  // effect's dependency stability rather than for cost.
  const retellingBlockIds = useMemo(
    () =>
      retellingModel.blocks
        .map((block) => block.id)
        .filter((id): id is string => id !== undefined),
    [retellingModel],
  );

  // Reads and re-reads of this chapter's paragraphs. Bound to the retelling
  // column, which is the pane a "block read" is about; `columnKey` re-binds the
  // observer when the paned/interleaved swap replaces that node.
  useReadingTelemetry({
    containerRef: retellingRef,
    bookSlug: book.slug,
    unitKey: String(chapter.number),
    blockIds: retellingBlockIds,
    columnKey,
  });

  const { currentLocation, jumpTo } = useReadingNav({
    retellingRef,
    model: retellingModel,
    columnKey,
    // Resume-at-position (R7a) is the READER's, not the hook's: the editor
    // workbench renders these same panes and must neither overwrite nor resume
    // from the admin's own reading position.
    onLocationChange: (location) => {
      savePosition(book.slug, chapter.number, location);
    },
    restorePosition: () =>
      getPosition(book.slug, chapter.number)?.location ?? null,
  });

  // Log the chapter open once per (book, chapter). Device context rides along
  // because retells has no session-start event — this is the once-per-unit
  // event the CPU telemetry gets sliced by device class through.
  useEffect(() => {
    logRef.current.event("reading.open", {
      book: book.slug,
      chapter: chapter.number,
      sourceLanguage: book.sourceLanguage,
      ...getDeviceContext(),
    });
    // Closes the long-task chunk covering everything up to this unit opening
    // (the previous unit's reading, plus this one's load and first layout).
    flushPerformanceChunk("unit_open", {
      book: book.slug,
      chapter: chapter.number,
    });
  }, [book.slug, book.sourceLanguage, chapter.number]);

  // Two independent scroll panes — the state the align and link toggles below
  // are only meaningful in. `ReadingPanes` derives this same predicate itself
  // (as `showOriginal && !interleaved`) to gate the sync hooks it owns.
  const twoPane = showOriginal && !isNarrow;
  useCopyHandler({
    retellingRef,
    bookTitle: book.title,
    columnKey,
    onCopy: (info) => {
      logRef.current.event("reading.copy", {
        book: book.slug,
        chapter: chapter.number,
        length: info.length,
        truncated: info.truncated,
      });
    },
  });

  const commitFont = (next: FontSizePrefs, pane: PaneId): void => {
    setFontSize(next);
    setPref("fontSize", next);
    logRef.current.event("reading.font_size", {
      book: book.slug,
      chapter: chapter.number,
      pane,
      size: next[pane],
      linked: next.linked,
    });
  };
  // Merge one param into the *live* URL (not react-router's possibly-stale
  // `searchParams` snapshot) and replace — not push — history: useReadingNav
  // writes `?loc=` via raw `history.replaceState`/`pushState` (bypassing
  // react-router's own history instance), so react-router's cached location
  // can lag behind the real URL. Reading `window.location.search` fresh here
  // guarantees `loc` is preserved rather than clobbered back to whatever
  // react-router last observed. `replace: true` keeps toggling from spamming
  // browser history the way scroll-driven `loc` updates already avoid it.
  const setUrlParam = (name: string, value: string | null): void => {
    const next = new URLSearchParams(window.location.search);
    if (value === null) next.delete(name);
    else next.set(name, value);
    setSearchParams(next, { replace: true });
  };

  const toggleOriginal = (open: boolean): void => {
    setShowOriginal(open);
    setUrlParam("original", open ? "1" : null);
    logRef.current.event("reading.original_toggle", {
      book: book.slug,
      chapter: chapter.number,
      open,
    });
  };
  // One event per stretch a reader actually opens in the interleaved layout —
  // the signal for "did weaving the original in get it read". Deliberately not
  // paired with a collapse event, and never per-scroll (logging policy §9).
  const expandOriginal = (): void => {
    logRef.current.event("reading.original_expand", {
      book: book.slug,
      chapter: chapter.number,
    });
  };
  const changeSyncMode = (mode: SyncMode): void => {
    setSyncMode(mode);
    setPref("syncMode", mode);
    setUrlParam("sync", mode === "interpolated" ? "interpolated" : null);
    logRef.current.event("reading.sync_mode", {
      book: book.slug,
      chapter: chapter.number,
      mode,
    });
  };

  const currentBookmarked = currentLocation
    ? isBookmarked(book.slug, chapter.number, currentLocation)
    : false;
  const toggleBookmark = (): void => {
    if (!currentLocation) return;
    const next = currentBookmarked
      ? removeBookmark(book.slug, chapter.number, currentLocation)
      : addBookmark(book.slug, chapter.number, currentLocation);
    setBookmarks(next);
    logRef.current.event(
      currentBookmarked ? "reading.bookmark_remove" : "reading.bookmark_add",
      { book: book.slug, chapter: chapter.number },
    );
  };

  // Bookmarks, view-mode toggles, and prev/next chevrons — everything but the
  // back button and title. Shared between the wide single-row toolbar and the
  // narrow layout's second row (below) so the two never drift apart.
  //
  // The font-link toggle is NOT here any more: it lives on the divide between
  // the two text-size controls it links (`reading-panes.tsx`), which is where a
  // reader looks for it.
  const toolbarControls: ReactNode = (
    <>
      <BookmarksMenu
        bookmarks={bookmarks}
        currentBookmarked={currentBookmarked}
        onToggle={toggleBookmark}
        onJump={jumpTo}
        onRemove={(location) => {
          setBookmarks(removeBookmark(book.slug, chapter.number, location));
        }}
      />

      <Separator orientation="vertical" className="h-6" />

      {/* View-mode cluster: hard-align only appears once the original pane is
          showing AND there are two panes to align; the original toggle itself
          is always present. Grouped together (own gap-1) and set off from the
          bookmarks cluster by the separator above. */}
      <div className="flex items-center gap-1">
        {twoPane && (
          <Toggle
            size="sm"
            pressed={syncMode === "hard"}
            onPressedChange={(pressed) => {
              changeSyncMode(pressed ? "hard" : "interpolated");
            }}
            aria-label="Hard-align the panes"
            title="Hard alignment"
          >
            <Rows2 className="size-4" />
          </Toggle>
        )}
        <Toggle
          size="sm"
          pressed={showOriginal}
          onPressedChange={toggleOriginal}
          aria-label="Show the original alongside"
          title="Original text"
        >
          <Columns2 className="size-4" />
        </Toggle>
      </div>

      <Separator orientation="vertical" className="h-6" />

      <UnitNavButtons bookSlug={book.slug} prev={prev} next={next} />
    </>
  );

  return (
    <div className="bg-background flex h-svh flex-col">
      {/* Top toolbar. The site controls (account, theme) are the tail of this
          same row via `AppNav` rather than a fixed cluster floating over it —
          that overlap is what the old `pr-16` reservation was for, and there is
          nothing left to reserve against. Below `md`, the bookmarks/view-mode/
          nav controls move to a second row: the title alone plus five sibling
          controls has nowhere near 314px of content width to share at phone
          size, and this book's titles are far longer than "Peter Pan" —
          squeezing everything into one row truncated the title to nothing. */}
      <header className="border-border flex flex-col border-b">
        <div className="flex items-center gap-2 px-3 py-2">
          <Button
            asChild
            variant="ghost"
            size="icon-sm"
            aria-label="Back to library"
          >
            <Link to="/">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <p className="font-display text-foreground truncate text-sm font-bold">
              {book.title}
            </p>
            <p className="text-muted-foreground truncate font-serif text-xs">
              {chapter.number}. {chapter.title}
            </p>
          </div>

          <AppNav variant="inline">{!isNarrow && toolbarControls}</AppNav>
        </div>

        {isNarrow && (
          <div className="border-border/70 flex items-center gap-2 border-t px-3 py-1.5">
            {toolbarControls}
          </div>
        )}
      </header>

      {/* `<main>` landmark around the reading content — this was the page's
          only content region but had no containing landmark (axe
          landmark-one-main/region), same fix as matter-view.tsx. It now also
          wraps the narrow-screen text-size bar below, which used to sit
          outside any landmark as a `main`-adjacent sibling (axe still flagged
          `region` on it alone even once the panes were covered). `flex-col`
          + `flex-1` on `<main>` itself takes over the vertical-fill job the
          old wrapper `div` did directly on the outer `h-svh flex-col`
          container; the panes row below keeps its own `flex-1` to fill
          whatever height the bar (when present) leaves it, so both layouts'
          scroll geometry (min-h-0/flex-1 doing the real work) is unchanged. */}
      <main className="flex min-h-0 flex-1 flex-col">
        {/* The panes, shared with the editor workbench. `showPaneBars` carries
            the breakpoint down rather than the panes measuring it themselves:
            with it off there is ONE unlabelled text-size bar above the column
            (its own row below the header, so the header's title keeps its
            width), with it on there is one LABELLED bar spanning both panes,
            each half naming the column under it. That split
            is not derivable from `interleaved` — narrow with the original
            closed is not interleaved but still wants the single bar. Font
            changes come back through `commitFont`, which is where the
            persistence lives. */}
        <ReadingPanes
          retellingModel={retellingModel}
          originalModel={originalModel}
          anchors={anchors}
          retellingRef={retellingRef}
          originalRef={originalRef}
          showOriginal={showOriginal}
          interleaved={interleaved}
          syncMode={syncMode}
          fontSize={fontSize}
          onFontChange={commitFont}
          showPaneBars={!isNarrow}
          glossEligible={glossEligible}
          onExpandOriginal={expandOriginal}
          retellingFooter={
            <PageTurnNav
              bookSlug={book.slug}
              prev={prev}
              next={next}
              volumeEnd={<VolumeEnd book={book} unit={unit} />}
            />
          }
        />
      </main>

      {/* Gloss/translation popover — original text only, non-English only.
          Gating on `glossEligible` (the same `sourceLanguage !== "en"` that
          also gates the `glossEntry` keyboard entry points above) keeps it
          structurally impossible for English books. The root is whichever
          container holds the original's runs — its own pane on desktop, the
          shared interleaved column on mobile; inside it, the layer resolves
          only `[data-pane="original"]` subtrees, so a selection in retold
          prose can never be read as an offset into the original. */}
      {glossEligible && showOriginal && (
        <SelectionLayer
          model={originalModel}
          rootRef={interleaved ? retellingRef : originalRef}
          bookSlug={book.slug}
          chapterNumber={chapter.number}
          sourceLanguage={book.sourceLanguage}
        />
      )}

      {/* Persistent copyright notice */}
      <footer className="border-border border-t px-4 py-1.5">
        <p className="text-muted-foreground truncate font-serif text-[11px]">
          {copyrightNotice(book.title)}
        </p>
      </footer>
    </div>
  );
}

/**
 * Chapter dispatch: a free chapter loads from the static corpus; a premium
 * (locked) chapter goes through the entitlement gate (plan 10 §4). The catalog
 * carries the `premium` flag, so this decision needs no server round-trip.
 */
function ChapterLoader({
  bookSlug,
  chapterNumber,
}: {
  bookSlug: string;
  chapterNumber: number;
}) {
  const catalog = useCatalog();
  const book = findBook(catalog, bookSlug);
  const chapter = book ? findChapter(book, chapterNumber) : undefined;
  if (!book || !chapter) {
    return <ReadingNotFound label="That chapter could not be found." />;
  }
  if (chapter.premium) {
    // Keyed on the subject so the gate's held status (`useLastLoaded` below)
    // can never outlive the chapter it was asked about: page-turning between
    // two premium chapters reconciles this component in place, and a held
    // `entitled: true` from the previous chapter would render the gated
    // loader for a chapter the reader may not own.
    return (
      <PremiumChapterGate
        key={`${bookSlug}:${String(chapterNumber)}`}
        book={book}
        chapter={chapter}
      />
    );
  }
  return (
    <FreeChapterLoader bookSlug={bookSlug} chapterNumber={chapterNumber} />
  );
}

/** The free reading path — unchanged: static corpus → ChapterReader. */
function FreeChapterLoader({
  bookSlug,
  chapterNumber,
}: {
  bookSlug: string;
  chapterNumber: number;
}) {
  const data = useChapterData(bookSlug, chapterNumber);
  if (!data) {
    return <ReadingNotFound label="That chapter could not be found." />;
  }
  return (
    <ChapterReader key={`${bookSlug}:${String(chapterNumber)}`} data={data} />
  );
}

/**
 * The entitlement gate for a locked chapter. Branches on the live
 * `entitlementStatus` query: loading → skeleton; entitled (or admin) → the
 * gated reader; otherwise the upsell. Server-side enforcement is
 * premium.getChapter itself — this branching is UX only, and a lapsed session
 * reports `authed: false`, degrading to a sign-in prompt rather than an error.
 */
function PremiumChapterGate({
  book,
  chapter,
}: {
  book: CatalogBook;
  chapter: CatalogChapter;
}) {
  // THE CHAPTER'S OWN VOLUME PRODUCT, not the book's — `lockedChapter` reads
  // `chapter.premiumProductId` for it. `book.premium.productId` is the
  // COMPLETE-TITLE BUNDLE id for a multi-volume title (`wholeTitleProductId`),
  // and the series fan-out runs bundle → volumes only: a reader who bought
  // volume two holds `odyssey-vol2-premium`, which never satisfies a question
  // about `odyssey-premium`. Gating on that id would leave them staring at the
  // upsell for a chapter `premium.getChapter` is perfectly willing to serve — a
  // lockout, not a leak, and the one this gate decides alone.
  const unit = lockedChapter(chapter);
  // The chapter is its own grant target: a volume-scoped comp is a chapter
  // range, tested against this number.
  const gate = useEntitlementGate(book, unit, chapter.number);
  if (gate.kind === "loading") return <ReadingSkeleton />;
  if (gate.kind === "entitled") {
    return (
      <EntitledChapterLoader
        bookSlug={book.slug}
        chapterNumber={chapter.number}
      />
    );
  }
  return <UpsellPlate book={book} unit={unit} status={gate.status} />;
}

/** Load an entitled premium chapter's data through the gated query. */
function EntitledChapterLoader({
  bookSlug,
  chapterNumber,
}: {
  bookSlug: string;
  chapterNumber: number;
}) {
  const data = usePremiumChapterData(bookSlug, chapterNumber);
  if (data === "loading") return <ReadingSkeleton />;
  if (data === null) {
    return <ReadingNotFound label="That chapter could not be found." />;
  }
  return (
    <ChapterReader key={`${bookSlug}:${String(chapterNumber)}`} data={data} />
  );
}

/**
 * `/read/:bookSlug/:unit` dispatch (PD-2): an all-digits unit is a chapter
 * number; anything else is a matter slug (matter slugs are validated
 * non-numeric at corpus build). Both loaders resolve against the catalog and
 * render not-found for units the book doesn't have.
 */
function UnitLoader() {
  const params = useParams();
  const bookSlug = params.bookSlug ?? "";
  const unit = params.unit ?? "";
  if (/^\d+$/.test(unit)) {
    return <ChapterLoader bookSlug={bookSlug} chapterNumber={Number(unit)} />;
  }
  return <MatterLoader bookSlug={bookSlug} matterSlug={unit} />;
}

export function ReadingView() {
  return (
    <ReadingFrame>
      <UnitLoader />
    </ReadingFrame>
  );
}
